import React, { useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import { useTitle } from '../hooks/useTitle';
import { logger } from '../helpers/logger';
import { RotatingDotsLoader } from '../components/progress/RotatingDotsLoader';
import {
    Navigate,
    NavLink,
    Link,
    BrowserRouter,
    MemoryRouter,
    HashRouter,
    Routes,
    useNavigate,
    useLocation,
    useMatch,
    useParams,
    useSearchParams,
    generatePath,
    createSearchParams,
    matchPath,
    matchRoutes,
    useBlocker,
} from 'react-router-dom';
import { initNavigation } from './navigationService';

export {
    Navigate,
    NavLink,
    Link,
    BrowserRouter,
    MemoryRouter,
    HashRouter,
    Routes,
    useNavigate,
    useLocation,
    useMatch,
    useParams,
    useSearchParams,
    generatePath,
    createSearchParams,
    matchPath,
    matchRoutes,
};

// React Router v5 → v6 compatibility shims

// Switch → Routes
export { Routes as Switch };

// Redirect → Navigate (v5 used <Redirect to="..." />, v6 uses <Navigate to="..." replace />)
export { Navigate as Redirect };

// TitledRoute — wraps children in a Suspense + page-title side-effect.
// Use as the `element` prop of a native <Route>:
//   <Route path="/foo" element={<TitledRoute title="Foo"><Foo /></TitledRoute>} />
export function TitledRoute({ title, appendToTitle, noTitle = false, children }) {
    useTitle(
        title
            ? appendToTitle
                ? `CareContinuity » ${title} » ${appendToTitle}`
                : `CareContinuity » ${title}`
            : null,
    );

    if (!title && !noTitle && title !== '')
        logger.error('⚠ route is missing a page title! 👎');

    return <Suspense fallback={<RotatingDotsLoader />}>{children ?? null}</Suspense>;
}

// useHistory → useNavigate wrapped to expose the v5 history object shape
export function useHistory() {
    const navigate = useNavigate();
    const location = useLocation();

    const push = useCallback(
        (to, state) => {
            if (to && typeof to === 'object' && !Array.isArray(to)) {
                const { pathname, search, hash, state: toState } = to;
                navigate({ pathname, search, hash }, { state: state ?? toState });
            } else {
                navigate(to, state ? { state } : undefined);
            }
        },
        [navigate],
    );

    const replace = useCallback(
        (to, state) => {
            if (to && typeof to === 'object' && !Array.isArray(to)) {
                const { pathname, search, hash, state: toState } = to;
                navigate({ pathname, search, hash }, { replace: true, state: state ?? toState });
            } else {
                navigate(to, { replace: true, ...(state ? { state } : {}) });
            }
        },
        [navigate],
    );

    const go = useCallback((delta) => navigate(delta), [navigate]);
    const goBack = useCallback(() => navigate(-1), [navigate]);
    const goForward = useCallback(() => navigate(1), [navigate]);

    return useMemo(
        () => ({ push, replace, go, goBack, goForward, location }),
        [go, goBack, goForward, location, push, replace],
    );
}

// useRouteMatch → split into two hooks to avoid conditional hook calls.
// useRouteMatch() without args uses useParams() + location (v5 "current route" pattern).
// useRouteMatch(pattern) delegates to useRouteMatchPattern(pattern).
export function useRouteMatch(pattern) {
    const location = useLocation();
    const currentParams = useParams();
    // Always call useMatch — pass location.pathname as a fallback when no pattern given
    // so the hook call count stays stable across renders.
    const resolvedPattern = pattern ?? location.pathname;
    const patternMatch = useMatch(resolvedPattern);

    if (pattern) {
        if (!patternMatch) return null;
        return {
            path: patternMatch.pattern.path,
            url: patternMatch.pathname,
            params: patternMatch.params,
            isExact: patternMatch.pathname === location.pathname,
        };
    }

    // No pattern: mirror v5 behaviour of returning the matched route's info
    return {
        path: location.pathname,
        url: location.pathname,
        params: currentParams,
        isExact: true,
    };
}

export function AppInsightsNavigationTracker({ history }) {
    const location = useLocation();
    const historyRef = useRef(history);
    useEffect(() => {
        historyRef.current.replace(location.pathname + location.search);
    }, [location]);
    return null;
}

export function NavigationInitializer() {
    const navigate = useNavigate();
    useEffect(() => {
        initNavigation(navigate);
    }, [navigate]);
    return null;
}

export const Prompt = ({ when, message }) => {
    const blocker = useBlocker(when);

    useEffect(() => {
        if (blocker.state !== 'blocked') return;
        if (window.confirm(message)) {
            blocker.proceed();
        } else {
            blocker.reset();
        }
    }, [blocker, message]);

    useEffect(() => {
        if (!when) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = message;
            return message;
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [message, when]);

    return null;
};
